import React, { useState, useEffect } from 'react';
import { useZones } from '../hooks/useZones';
import { useBoxes } from '../hooks/useBoxes';
import { usePieces } from '../hooks/usePieces';
import { MapPin, Package, DoorOpen } from 'lucide-react';

interface LocationSelectorProps {
  currentZoneId?: string | number;
  currentBoxId?: string | number;
  currentPieceId?: string | number;
  onLocationChange: (zoneId?: string, boxId?: string, pieceId?: string) => void;
  disabled?: boolean;
  showCurrentLocation?: boolean;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  currentZoneId,
  currentBoxId,
  currentPieceId,
  onLocationChange,
  disabled = false,
  showCurrentLocation = true,
}) => {
  const { data: zones = [] } = useZones();
  const { data: boxes = [] } = useBoxes();
  const { data: pieces = [] } = usePieces();

  const [locationType, setLocationType] = useState<'zone' | 'box' | 'piece'>('zone');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedBox, setSelectedBox] = useState('');
  const [selectedPiece, setSelectedPiece] = useState('');

  const [initialZoneId] = useState(currentZoneId);
  const [initialBoxId] = useState(currentBoxId);
  const [initialPieceId] = useState(currentPieceId);

  useEffect(() => {
    if (initialZoneId) {
      setLocationType('zone');
      setSelectedZone(String(initialZoneId));
    } else if (initialBoxId) {
      setLocationType('box');
      setSelectedBox(String(initialBoxId));
    } else if (initialPieceId) {
      setLocationType('piece');
      setSelectedPiece(String(initialPieceId));
    }
  }, [initialZoneId, initialBoxId, initialPieceId]);

  const handleLocationTypeChange = (type: 'zone' | 'box' | 'piece') => {
    setLocationType(type);
    setSelectedZone('');
    setSelectedBox('');
    setSelectedPiece('');
    onLocationChange(undefined, undefined, undefined);
  };

  const handleZoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedZone(value);
    onLocationChange(value || undefined, undefined, undefined);
  };

  const handleBoxChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedBox(value);
    onLocationChange(undefined, value || undefined, undefined);
  };

  const handlePieceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedPiece(value);
    onLocationChange(undefined, undefined, value || undefined);
  };

  const getCurrentLocation = () => {
    if (initialZoneId) {
      const zone = zones.find((z: any) => String(z.id) === String(initialZoneId));
      return zone ? `🏠 ${zone.code} - ${zone.nom}` : `Zone ${initialZoneId}`;
    }
    if (initialBoxId) {
      const box = boxes.find((b: any) => String(b.id) === String(initialBoxId));
      return box ? `📦 ${box.code} - ${box.nom}` : `Box ${initialBoxId}`;
    }
    if (initialPieceId) {
      const piece = pieces.find((p: any) => String(p.id) === String(initialPieceId));
      return piece ? `🚪 ${piece.nom}${piece.numero ? ` (${piece.numero})` : ''}` : `Pièce ${initialPieceId}`;
    }
    return 'Aucun emplacement défini';
  };

  return (
    <div className="space-y-4">
      {showCurrentLocation && (initialZoneId || initialBoxId || initialPieceId) && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs font-medium text-blue-900 mb-1">📍 Emplacement actuel</p>
          <p className="text-sm text-blue-800 font-medium">{getCurrentLocation()}</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Type d'emplacement
        </label>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => !disabled && handleLocationTypeChange('piece')}
            disabled={disabled}
            className={`p-3 border-2 rounded-lg transition-all ${
              locationType === 'piece'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <DoorOpen className="h-5 w-5 mx-auto mb-1 text-emerald-600" />
            <div className="text-sm font-medium text-gray-900">Pièce</div>
            <div className="text-xs text-gray-600">Salle / local</div>
          </button>
          <button
            type="button"
            onClick={() => !disabled && handleLocationTypeChange('zone')}
            disabled={disabled}
            className={`p-3 border-2 rounded-lg transition-all ${
              locationType === 'zone'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <MapPin className="h-5 w-5 mx-auto mb-1 text-blue-600" />
            <div className="text-sm font-medium text-gray-900">Zone</div>
            <div className="text-xs text-gray-600">Armoire fixe</div>
          </button>
          <button
            type="button"
            onClick={() => !disabled && handleLocationTypeChange('box')}
            disabled={disabled}
            className={`p-3 border-2 rounded-lg transition-all ${
              locationType === 'box'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <Package className="h-5 w-5 mx-auto mb-1 text-purple-600" />
            <div className="text-sm font-medium text-gray-900">Box</div>
            <div className="text-xs text-gray-600">Caisse mobile</div>
          </button>
        </div>
      </div>

      {locationType === 'piece' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pièce de destination
          </label>
          <select
            value={selectedPiece}
            onChange={handlePieceChange}
            disabled={disabled}
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
          >
            <option value="">Sélectionner une pièce</option>
            {pieces.map((piece: any) => (
              <option key={piece.id} value={piece.id}>
                {piece.nom}{piece.numero ? ` (${piece.numero})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {locationType === 'zone' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zone de destination
          </label>
          <select
            value={selectedZone}
            onChange={handleZoneChange}
            disabled={disabled}
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
          >
            <option value="">Sélectionner une zone</option>
            {zones.map((zone: any) => (
              <option key={zone.id} value={zone.id}>
                {zone.code} - {zone.nom}
              </option>
            ))}
          </select>
        </div>
      )}

      {locationType === 'box' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Box de destination
          </label>
          <select
            value={selectedBox}
            onChange={handleBoxChange}
            disabled={disabled}
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
          >
            <option value="">Sélectionner une box</option>
            {boxes.map((box: any) => (
              <option key={box.id} value={box.id}>
                {box.code} - {box.nom}
              </option>
            ))}
          </select>
        </div>
      )}

      {!disabled && (selectedZone || selectedBox || selectedPiece) && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            ✅ Nouvel emplacement sélectionné. Enregistrez pour confirmer.
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
